import React, { useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/use-subscription";
import { useAnalytics } from "@/hooks/use-analytics";

export const UsageStats = () => {
  const { 
    isSubscribed, 
    isTrialing, 
    openPricingModal, 
    queryCount, 
    queryLimit, 
    calendarDaysLimit 
  } = useSubscription();
  
  const analytics = useAnalytics();
  
  const queriesUsed = queryCount || 0;
  const remainingQueries = queryLimit !== Infinity ? Math.max(0, queryLimit - queriesUsed) : Infinity;
  const queryPercentage = queryLimit !== Infinity && queryLimit > 0
    ? Math.min(100, Math.round((queriesUsed / queryLimit) * 100))
    : 0;
  
  useEffect(() => {
    if (!isSubscribed) {
      analytics.trackEvent('usage_stats_viewed', {
        queries_used: queriesUsed,
        queries_remaining: remainingQueries,
        query_limit: queryLimit,
        is_trial: isTrialing
      });
    }
  }, [analytics, queriesUsed, remainingQueries, queryLimit, isTrialing, isSubscribed]);
  
  if (isSubscribed) return null;
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Your Usage</h3>
      
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>AI Queries Used</span>
            <span className="font-medium">{queriesUsed} / {queryLimit === Infinity ? '∞' : queryLimit}</span>
          </div>
          {queryLimit !== Infinity && (
            <Progress value={queryPercentage} className="h-2" />
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {queryLimit === Infinity 
              ? "Unlimited queries with your current plan." 
              : `${remainingQueries} queries remaining ${isTrialing ? "in your trial" : "in free tier"}`}
          </p>
        </div>
        
        {!isTrialing && !isSubscribed && calendarDaysLimit !== Infinity && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Calendar Date Range</span>
              <span className="font-medium">±{calendarDaysLimit} days</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Free users can access calendar data from {calendarDaysLimit} days before/after today
            </p>
          </div>
        )}
        
        <Button 
          onClick={() => openPricingModal('usage_stats')}
          className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
        >
          {isTrialing ? "Upgrade Before Trial Ends" : "Upgrade for Unlimited Access"}
        </Button>
      </div>
    </div>
  );
};
